import { queryClient } from 'v2/configs/rq-setup'
import { useGetAuthIdAndTokenQuery } from 'v2/pages/settings/queries'
import { useAllSubaccountsQuery } from 'v2/shared/hooks/use-all-subaccounts'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useFlowStore } from '../store'
import type { FlowData } from '../types'

interface StartNodeAuthData {
  authId: string
  // Optional to mirror RAuthDetails: the MA token is omitted for sessions without
  // `api_credentials view`, and the panel then shows the restricted-token field.
  authToken?: string
}

/**
 * Resolves the identity (auth id + token) the Start node's "API Request"
 * panel and the "Copy cURL"/code-snippet generators should show/embed for
 * THIS flow:
 *  - untagged flow -> the main account pair (unchanged: useGetAuthIdAndTokenQuery).
 *  - tagged flow -> the SUBACCOUNT's own auth_id + auth_token, so a tagged
 *    flow's requests are authenticated as the subaccount it actually runs
 *    under, not the main account.
 *
 * Token source: the internal `/subaccounts` list, asked for WITH auth tokens
 * (useAllSubaccountsQuery's `withAuthTokens` variant — the only caller that
 * needs them; every other surface loads the names-only list). The
 * obvious-looking alternative — the existing settingsService.getSubaccount
 * (subAuthId) — proxies to Plivo's public `/plivo-api/Subaccount/{id}/` REST
 * resource (a different backend than the `/subaccounts` internal list), which
 * does not return auth_token on a plain GET (matching the main Account API's
 * own GET semantics — the token is only ever surfaced on create/regenerate).
 * The internal list DOES return each subaccount's real auth_token (confirmed
 * by the Subaccounts management page's AuthTokenCell) — so this hook reads it
 * from there instead of adding a call to an endpoint that can't answer it.
 *
 * A subaccount beyond the list's ~10k browse cap resolves to `authData:
 * undefined` (same "not ready yet" shape the panel already handles) rather
 * than ever falling back to the main account's credentials.
 *
 * Which tag is used: the STAGED one from agentSettings when the user has
 * picked a Sub Account on the Settings tab, otherwise the persisted one on
 * the flow. Reading only the persisted tag left the panel showing the old
 * account's credentials until a save landed. The token-bearing list is
 * fetched once per session for a tagged flow and cached, so the swap costs no
 * extra request. Subscribing to the store is also what makes the panel
 * re-render on the pick — the queryClient read below is a plain cache read
 * with no reactivity of its own.
 */
interface StartNodeAuthResult {
  authData: StartNodeAuthData | undefined
  subAuthId: string | null
}

export const useStartNodeAuthData = (
  flowId: string | undefined,
  type: string | undefined,
): StartNodeAuthResult => {
  const { data: mainAuth } = useGetAuthIdAndTokenQuery(type || '')
  const flowData = queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(flowId || ''))
  const stagedSubAuthId = useFlowStore((state) => state.agentSettings?.sub_auth_id)
  // `!== undefined` is load-bearing: agentSettings is seeded asynchronously and
  // is shallow-merged, so it can exist without a sub_auth_id key. undefined =
  // nothing staged, use the server value; null = explicitly untagged.
  const subAuthId =
    stagedSubAuthId !== undefined ? stagedSubAuthId : (flowData?.phlo?.sub_auth_id ?? null)
  const { data: subaccountsResult } = useAllSubaccountsQuery(!!subAuthId, { withAuthTokens: true })
  const subaccounts = subaccountsResult?.subaccounts

  if (!subAuthId) {
    return { authData: mainAuth?.data, subAuthId: null }
  }

  const taggedSubaccount = subaccounts?.find((subaccount) => subaccount.auth_id === subAuthId)
  const authData: StartNodeAuthData | undefined = taggedSubaccount
    ? { authId: taggedSubaccount.auth_id, authToken: taggedSubaccount.auth_token || '' }
    : undefined

  return { authData, subAuthId }
}
